// src/hooks/useAuth.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin, logout as apiLogout, checkAuth } from '../services/api';
import { useAlert } from './useAlert';

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { showAlert } = useAlert();

    const isAuthenticated = !!user; // Convert user to a boolean

    useEffect(() => {
        const checkAuthStatus = async () => {
            console.log("[AUTH CLIENT] Checking authentication status on page load...");
            try {
                const response = await checkAuth();
                if (response.data && response.data.user) {
                    console.log("[AUTH CLIENT] User is authenticated. User data:", response.data.user);
                    setUser(response.data.user);
                } else {
                    console.log("[AUTH CLIENT] No active session found.");
                    setUser(null);
                }
            } catch (error) {
                console.warn("[AUTH CLIENT] Check auth failed. User is not logged in.");
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        checkAuthStatus();
    }, []);

    const login = async (credentials) => {
        console.log("[AUTH CLIENT] Login function called. Sending request to server...");
        try {
            const response = await apiLogin(credentials);
            console.log("[AUTH CLIENT] Login request successful. Server response:", response.data);
            if (response.data && response.data.user) {
                setUser(response.data.user);
                showAlert('Login successful!', 'success');
                if (response.data.user.role === 'super_admin') {
                    navigate('/admin');
                } else {
                    navigate('/dashboard');
                }
            } else {
                throw new Error("Login response did not contain user data.");
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'An unknown error occurred during login.';
            console.error("[AUTH CLIENT] Login failed:", errorMessage);
            showAlert(errorMessage, 'error');
        }
    };

    const logout = async () => {
        console.log("[AUTH CLIENT] Logout function called.");
        try {
            await apiLogout();
            setUser(null);
            navigate('/login');
        } catch (err) {
            showAlert('Logout failed.', 'error');
        }
    };

    const value = {
        user,
        loading,
        isAuthenticated,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
